import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, Laptop, Share, PlusSquare, MoreVertical, Compass, Info, CheckCircle, Monitor } from 'lucide-react';

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PwaHandlerProps {
  isStandalone: boolean;
  canInstall: boolean; // Native install prompt support (deferredPrompt != null)
  onInstall: () => void; // Trigger native install
}

const PwaHandler: React.FC<PwaHandlerProps> = ({ 
  isStandalone, 
  canInstall, 
  onInstall
}) => {
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [osName, setOsName] = useState<'ios' | 'android' | 'desktop'>('android');
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    const checkPlatformAndUa = () => {
      const ua = navigator.userAgent.toLowerCase();
      const isMobileUA = /iphone|ipad|ipod|android|blackberry|mini|windows\sphone|palm/i.test(ua);
      const hasTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
      const mobileStatus = isMobileUA || (hasTouch && window.innerWidth < 1024);
      
      setIsMobileDevice(mobileStatus);

      if (/iphone|ipad|ipod/.test(ua)) {
        setOsName('ios');
      } else if (/android/.test(ua)) {
        setOsName('android');
      } else {
        setOsName('desktop');
      }
    };
    
    checkPlatformAndUa();
    window.addEventListener('resize', checkPlatformAndUa);
    return () => window.removeEventListener('resize', checkPlatformAndUa);
  }, []);

  useEffect(() => {
    if (isStandalone) {
      setIsBannerVisible(false);
      return;
    }

    // Hide banner on desktops/laptops unless PWA programmatic install is supported natively (canInstall is true)
    if (!isMobileDevice && !canInstall) {
      setIsBannerVisible(false);
      return;
    }

    const dismissed = localStorage.getItem('pwa_banner_dismissed') === 'true';
    if (!dismissed) {
      const timer = setTimeout(() => {
        setIsBannerVisible(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isStandalone, isMobileDevice, canInstall]);

  const handleDismissBanner = () => {
    setIsBannerVisible(false);
    localStorage.setItem('pwa_banner_dismissed', 'true');
  };

  const handleInstallClick = () => {
    // If standard canInstall is available and not iOS, execute native prompt
    if (canInstall && osName !== 'ios') {
      onInstall();
    } else {
      // Show custom guided helper modal
      setShowGuide(true);
      // Trigger the native/profile actions programmatically if iOS
      if (osName === 'ios') {
        onInstall();
      }
    }
  };

  if (isStandalone) return null;

  return (
    <>
      {/* FLOATING ACTION BOTTOM BANNER */}
      {isBannerVisible && (
        <div 
          id="pwa-install-app-banner"
          className="fixed bottom-24 left-4 right-4 md:left-8 md:right-auto md:bottom-8 z-[2000] animate-fade-in-up max-w-sm w-full select-none"
        >
          <div className="bg-slate-950/95 border border-white/10 rounded-3rem p-4 md:p-5 flex items-center justify-between gap-4 shadow-2xl shadow-black/80 text-white relative overflow-hidden backdrop-blur-xl">
            {/* Subtle glow effect */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/10 rounded-full blur-2xl -z-10"></div>
            
            <div className="flex items-center gap-3.5 min-w-0">
              <div id="pwa-app-icon-badge" className="w-11 h-11 bg-indigo-600/15 border border-indigo-500/35 rounded-2xl flex items-center justify-center text-indigo-400 shrink-0 shadow-lg">
                {isMobileDevice ? (
                  <Smartphone className="animate-pulse" size={20} />
                ) : (
                  <Laptop className="animate-pulse" size={20} />
                )}
              </div>
              <div className="min-w-0">
                <h3 className="text-[11px] font-black text-slate-100 leading-tight tracking-wide flex items-center gap-1.5 uppercase font-sans">
                  Instalar App
                  <span className="inline-block bg-indigo-500 text-slate-950 font-black text-[8px] px-1.5 py-0.5 rounded-full font-mono">NATIVO</span>
                </h3>
                <p className="text-[10px] text-gray-400 mt-1 leading-normal font-medium max-w-[180px] font-sans">
                  Acesso ultra rápido direto na tela de aplicativos do seu celular ou PC!
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 shrink-0">
              <button 
                id="pwa-banner-install-btn"
                onClick={handleInstallClick}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all shadow-md active:scale-95 flex items-center gap-1.5 whitespace-nowrap cursor-pointer hover:scale-[1.03]"
              >
                <Download size={12} /> Instalar
              </button>
              <button 
                id="pwa-banner-close-btn"
                onClick={handleDismissBanner}
                className="p-2 text-slate-400 hover:text-white transition-colors cursor-pointer bg-slate-800/55 rounded-xl border border-slate-700/40"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DETAILED PWA STEP-BY-STEP INSTALLATION GUIDE MODAL */}
      {showGuide && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-fade-in">
          <div 
            onClick={(e) => e.stopPropagation()}
            className="relative bg-[#0b0922]/98 border border-white/10 rounded-[2.5rem] shadow-[0_30px_100px_rgba(0,0,0,0.8)] w-full max-w-lg overflow-hidden flex flex-col text-white"
          >
            {/* Header Area */}
            <div className="bg-gradient-to-r from-indigo-950 via-[#0b0922] to-slate-950 px-6 py-6 border-b border-white/5 relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none"></div>
              <button 
                onClick={() => setShowGuide(false)}
                className="absolute top-5 right-5 p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 hover:text-white transition-all cursor-pointer"
              >
                <X size={16} />
              </button>
              
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-indigo-600/20 border border-indigo-500/30 rounded-2xl flex items-center justify-center text-indigo-400 shrink-0">
                  <Smartphone size={24} />
                </div>
                <div>
                  <h3 className="text-sm font-black uppercase tracking-wider text-[#ffb900]">Instalador de Aplicativo</h3>
                  <p className="text-[11px] text-slate-300 font-medium">Instale agora na sua tela inicial em poucos passos</p>
                </div>
              </div>
            </div>

            {/* Platform Selection Tabs */}
            <div className="flex border-b border-white/5 p-2.5 bg-[#09071c]">
              <button
                onClick={() => setOsName('ios')}
                className={`flex-1 py-2 rounded-xl text-[10px] uppercase font-black tracking-wider transition-all flex items-center justify-center gap-1.5 z-10 ${
                  osName === 'ios' 
                    ? 'bg-indigo-600/25 border border-indigo-500/40 text-white shadow-inner' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Smartphone size={13} /> iPhone / iOS
              </button>
              <button
                onClick={() => setOsName('android')}
                className={`flex-1 py-2 rounded-xl text-[10px] uppercase font-black tracking-wider transition-all flex items-center justify-center gap-1.5 z-10 ${
                  osName === 'android' 
                    ? 'bg-indigo-600/25 border border-indigo-500/40 text-white shadow-inner' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Smartphone size={13} /> Android
              </button>
              <button
                onClick={() => setOsName('desktop')}
                className={`flex-1 py-2 rounded-xl text-[10px] uppercase font-black tracking-wider transition-all flex items-center justify-center gap-1.5 z-10 ${
                  osName === 'desktop' 
                    ? 'bg-indigo-600/25 border border-indigo-500/40 text-white shadow-inner' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Monitor size={13} /> Computador
              </button>
            </div>

            {/* Scrollable Step Guide */}
            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-5 custom-scrollbar text-slate-200">
              
              {osName === 'ios' && (
                <div className="space-y-4 animate-fade-in">
                  <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-2xl flex gap-3 text-amber-200 text-xs">
                    <Info size={22} className="shrink-0" />
                    <div>
                      <p className="font-extrabold uppercase text-[10px] tracking-wider mb-0.5">Instalação Recomendada para iPhone</p>
                      <p className="text-[11px] leading-relaxed opacity-90">O Safari no iOS necessita de uma rápida ativação manual. Siga as instruções ilustradas abaixo:</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Step 1 */}
                    <div className="flex gap-4 items-start bg-white/5 p-4 rounded-3xl border border-white/5">
                      <div className="w-7 h-7 rounded-xl bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">1</div>
                      <div className="text-xs">
                        <p className="font-bold text-white mb-1 tracking-wide flex items-center gap-1.5">
                          Abrir Menu Compartilhar <Share size={14} className="text-[#ffb900]" />
                        </p>
                        <p className="text-slate-300 leading-relaxed">No navegador Safari, localize e clique no botão de <strong>Compartilhar</strong> (ícone de quadrado com uma seta para cima, na barra de navegação).</p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex gap-4 items-start bg-white/5 p-4 rounded-3xl border border-white/5">
                      <div className="w-7 h-7 rounded-xl bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">2</div>
                      <div className="text-xs">
                        <p className="font-bold text-white mb-1 tracking-wide flex items-center gap-1.5">
                          "Adicionar à Tela de Início" <PlusSquare size={14} className="text-[#ffb900]" />
                        </p>
                        <p className="text-slate-300 leading-relaxed">Role as opções de compartilhamento para baixo e toque em <strong>"Adicionar à Tela de Início"</strong> (ou "Adicionar ao Ecrã Principal").</p>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="flex gap-4 items-start bg-white/5 p-4 rounded-3xl border border-white/5">
                      <div className="w-7 h-7 rounded-xl bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">3</div>
                      <div className="text-xs">
                        <p className="font-bold text-white mb-1 tracking-wide flex items-center gap-1.5">
                          Confirmar Instalação <CheckCircle size={14} className="text-emerald-400" />
                        </p>
                        <p className="text-slate-300 leading-relaxed">No canto superior direito da tela, clique em <strong>"Adicionar"</strong>. O ícone aparecerá instantaneamente como um app nativo!</p>
                      </div>
                    </div>
                  </div>

                  {/* Alternative Pro iOS Profile Section */}
                  <div className="pt-2 border-t border-white/5 mt-4">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 text-center">Bypass Avançado de Instalação</p>
                    <button
                      onClick={() => {
                        window.location.href = '/pwa-install.mobileconfig';
                      }}
                      className="w-full bg-slate-800 hover:bg-slate-700/80 border border-slate-700 text-slate-100 py-3 rounded-2xl text-[10px] font-black uppercase tracking-wider text-center transition-all cursor-pointer flex items-center justify-center gap-2 hover:scale-[1.01]"
                    >
                      <Download size={13} /> Baixar Perfil de Configuração Direto
                    </button>
                    <p className="text-[9px] text-slate-400 mt-2 text-center leading-relaxed">
                      Este método cria o atalho nativo automaticamente. Certifique-se de autorizar o perfil em <strong>Ajustes &gt; Geral &gt; Gestão de Perfis</strong> se solicitado.
                    </p>
                  </div>
                </div>
              )}

              {osName === 'android' && (
                <div className="space-y-4 animate-fade-in">
                  <div className="p-3 bg-indigo-500/5 border border-indigo-500/20 rounded-2xl flex gap-3 text-indigo-200 text-xs">
                    <Compass size={22} className="shrink-0 animate-spin" style={{ animationDuration: '6s' }} />
                    <div>
                      <p className="font-extrabold uppercase text-[10px] tracking-wider mb-0.5">Opções de Instalação no Android</p>
                      <p className="text-[11px] leading-relaxed opacity-90">Caso clicar em "Instalar" não tenha aberto um aviso nativo do seu navegador Chrome, conclua de forma manual:</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Step 1 */}
                    <div className="flex gap-4 items-start bg-white/5 p-4 rounded-3xl border border-white/5">
                      <div className="w-7 h-7 rounded-xl bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">1</div>
                      <div className="text-xs">
                        <p className="font-bold text-white mb-1 tracking-wide flex items-center gap-1.5">
                          Abrir o Menu do Chrome <MoreVertical size={14} className="text-[#ffb900]" />
                        </p>
                        <p className="text-slate-300 leading-relaxed">Localize o menu flutuante representado por <strong>três pontinhos verticais</strong> no canto superior direito do seu navegador Chrome.</p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex gap-4 items-start bg-white/5 p-4 rounded-3xl border border-white/5">
                      <div className="w-7 h-7 rounded-xl bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">2</div>
                      <div className="text-xs">
                        <p className="font-bold text-white mb-1 tracking-wide flex items-center gap-1.5">
                          Selecionar "Instalar aplicativo" <PlusSquare size={14} className="text-[#ffb900]" />
                        </p>
                        <p className="text-slate-300 leading-relaxed">Clique em <strong>"Instalar aplicativo"</strong> ou <strong>"Adicionar à Tela Principal / Inicial"</strong> na lista de opções que surgir.</p>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="flex gap-4 items-start bg-white/5 p-4 rounded-3xl border border-white/5">
                      <div className="w-7 h-7 rounded-xl bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">3</div>
                      <div className="text-xs">
                        <p className="font-bold text-white mb-1 tracking-wide flex items-center gap-1.5">
                          Confirmar Instalação <CheckCircle size={14} className="text-emerald-400" />
                        </p>
                        <p className="text-slate-300 leading-relaxed">Confirme no modal e divirta-se! O aplicativo agora iniciará de forma independente sem as barras de pesquisa do Chrome.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {osName === 'desktop' && (
                <div className="space-y-4 animate-fade-in">
                  <div className="p-3 bg-zinc-800 border border-white/5 rounded-2xl flex gap-3 text-slate-300 text-xs">
                    <Monitor size={22} className="shrink-0" />
                    <div>
                      <p className="font-extrabold uppercase text-[10px] tracking-wider mb-0.5">Instalar no Coputador</p>
                      <p className="text-[11px] leading-relaxed opacity-90">Inicie de forma instantânea como um aplicativo nativo exclusivo e com carregamento instantâneo:</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Step 1 */}
                    <div className="flex gap-4 items-start bg-white/5 p-4 rounded-3xl border border-white/5">
                      <div className="w-7 h-7 rounded-xl bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">1</div>
                      <div className="text-xs">
                        <p className="font-bold text-white mb-1 tracking-wide flex items-center gap-1.5">
                          Barra de Endereços (+)
                        </p>
                        <p className="text-slate-300 leading-relaxed">Olhe no topo direito da barra de links (URLs) do seu navegador Chrome, Edge ou Brave. Haverá um ícone de <strong>"+"</strong> ou um símbolo de monitor com uma seta.</p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex gap-4 items-start bg-white/5 p-4 rounded-3xl border border-white/5">
                      <div className="w-7 h-7 rounded-xl bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">2</div>
                      <div className="text-xs">
                        <p className="font-bold text-white mb-1 tracking-wide flex items-center gap-1.5">
                          Clique em "Instalar"
                        </p>
                        <p className="text-slate-300 leading-relaxed">Clique sobre ele e selecione a confirmação de instalação para o aplicativo se separar do navegador standard.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-[#08061a] p-5 border-t border-white/5 flex justify-end">
              <button 
                onClick={() => setShowGuide(false)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-[11px] uppercase tracking-wider px-6 py-3 rounded-2xl cursor-pointer transition-all active:scale-95 hover:scale-[1.02]"
              >
                Entendi, Prontinho
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PwaHandler;
